import React, { useState, useEffect } from 'react';
import { Spinner, Modal, Form, Button, Alert, Card, Row, Col } from 'react-bootstrap';
import { useAuth } from '../../hooks/useAuth';
import { FaUser, FaKey, FaBell, FaShieldAlt } from 'react-icons/fa';
import './Settings.css'; // We'll reuse the DSO Settings CSS
import api, { backofficeApi } from '../../services/api';

function BackofficeSettings() {
  const [activeSection, setActiveSection] = useState('profile');
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notifications, setNotifications] = useState(true);
  const { getCurrentUser } = useAuth();
  
  // Form data for editing profile
  const [formData, setFormData] = useState({
    name: '',
    mobile: ''
  });
  const [updateSuccess, setUpdateSuccess] = useState('');
  const [updateError, setUpdateError] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);

  // Password reset modal state
  const [showResetModal, setShowResetModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');

  // Navigation items for settings
  const navItems = [
    { id: 'profile', label: 'Profile Information', icon: <FaUser size={16} /> },
    { id: 'security', label: 'Security', icon: <FaKey size={16} /> },
    { id: 'notifications', label: 'Notifications', icon: <FaBell size={16} /> }
  ];
  
  // Modal handler functions
  const handleCloseModal = () => {
    setShowResetModal(false);
    // Reset modal-specific states
    setNewPassword('');
    setConfirmPassword('');
    setResetError('');
    setResetSuccess('');
    setResetLoading(false);
  };

  // Toggle handlers for notifications
  const toggleNotifications = () => {
    setNotifications(!notifications);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Submit profile update
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setUpdateError('');
    setUpdateSuccess('');
    setSaveLoading(true);

    try {
      const response = await backofficeApi.updateProfile({
        name: formData.name,
        mobile: formData.mobile
      });
      
      // Update local state
      setUserDetails(prev => ({
        ...prev,
        name: formData.name,
        mobile: formData.mobile
      }));
      
      setUpdateSuccess('Profile updated successfully!');
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setUpdateSuccess('');
      }, 3000);
    } catch (err) {
      console.error("Error updating profile:", err);
      setUpdateError('Failed to update profile. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handlePasswordResetSubmit = async (event) => {
    event.preventDefault();
    setResetError('');
    setResetSuccess('');

    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 5) {
      setResetError('Password must be at least 5 characters long.');
      return;
    }

    setResetLoading(true);
    try {
      await backofficeApi.resetPassword(newPassword);
      setResetSuccess('Password reset successfully!');
    } catch (err) {
      console.error("Error resetting password:", err);
      setResetError('Failed to reset password. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  // Load user details
  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await backofficeApi.getProfile();
        const profile = response.data;
        
        setUserDetails({
          id: profile.id,
          name: profile.name,
          email: profile.email,
          mobile: profile.mobile,
          is_active: profile.is_active
        });
        
        setFormData({
          name: profile.name,
          mobile: profile.mobile
        });
        
      } catch (err) {
        console.error("Error fetching user details:", err);
        
        // Fallback to local data if API fails
        const currentUser = getCurrentUser();
        setUserDetails({
          name: currentUser?.name || '',
          email: currentUser?.email || '',
          mobile: currentUser?.mobile || '',
          user_type: 'BACKOFFICE',
          is_active: true
        });
        
        setFormData({
          name: currentUser?.name || '',
          mobile: currentUser?.mobile || ''
        });
        
        setError('Failed to load profile information from server. Using local data.');
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [getCurrentUser]);

  // Render content based on active section
  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        if (loading) {
          return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px' }}>
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          );
        }
        
        if (error) {
          return <p style={{ color: 'red' }}>Error: {error}</p>;
        }
        
        return (
          <>
            <h3>Profile Information</h3>
            
            {updateSuccess && <Alert variant="success">{updateSuccess}</Alert>}
            {updateError && <Alert variant="danger">{updateError}</Alert>}
            
            {userDetails ? (
              <Form onSubmit={handleProfileSubmit}>
                <div className="dso-profile-details">
                  <h4 className="dso-section-title">Personal Information</h4>
                  
                  <div className="dso-detail-item">
                    <span className="dso-detail-label">Name:</span>
                    <Form.Control 
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="dso-detail-value"
                    />
                  </div>
                  
                  <div className="dso-detail-item">
                    <span className="dso-detail-label">Email:</span>
                    <span className="dso-detail-value">{userDetails.email || 'N/A'}</span>
                  </div>
                  
                  <div className="dso-detail-item">
                    <span className="dso-detail-label">Mobile Number:</span>
                    <Form.Control 
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      className="dso-detail-value"
                    />
                  </div>
                  
                  <div className="dso-detail-item">
                    <span className="dso-detail-label">Status:</span>
                    <span className="dso-detail-value">{userDetails.is_active ? 'Active' : 'Inactive'}</span>
                  </div>
                  
                  <div className="mt-3">
                    <Button 
                      type="submit" 
                      className="dso-btn dso-btn-primary" 
                      disabled={saveLoading}
                    >
                      {saveLoading ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Saving...
                        </>
                      ) : "Save Changes"}
                    </Button>
                  </div>
                </div>
              </Form>
            ) : (
              <p>Could not load profile data.</p>
            )}
          </>
        );
        
      case 'security':
        return (
          <>
            <h3>Security Settings</h3>
            <div className="dso-profile-details">
              <div className="dso-detail-item">
                <span className="dso-detail-label">Password:</span>
                <button
                  className="dso-btn dso-btn-primary"
                  onClick={() => {
                    setShowResetModal(true);
                  }}
                >
                  <FaKey/> Reset Password
                </button>
              </div>
            </div>
          </>
        );
        
      case 'notifications':
        return (
          <>
            <h3>Notification Preferences</h3>
            <div className="dso-profile-details">
              <div className="dso-detail-item">
                <span className="dso-detail-label">Notifications:</span>
                <div className="dso-toggle-container">
                  <label className="dso-toggle">
                    <input 
                      type="checkbox" 
                      checked={notifications} 
                      onChange={toggleNotifications} 
                    />
                    <span className="dso-toggle-slider"></span>
                  </label>
                  <span className="dso-toggle-status">
                    {notifications ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
          </>
        );
        
      default:
        return <p>Select a section from the menu</p>;
    }
  };

  return (
    <div className="dso-settings-page">
      <div className="dso-settings-content">
        {/* Left section - Navigation */}
        <div className="dso-settings-left">
          <ul className="dso-settings-nav">
            {navItems.map(item => (
              <li key={item.id} className="dso-settings-nav-item">
                <div 
                  className={`dso-settings-nav-link ${activeSection === item.id ? 'active' : ''}`}
                  onClick={() => setActiveSection(item.id)}
                >
                  <span className="dso-settings-nav-icon">{item.icon}</span>
                  <span>{item.label}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Right section - Content */}
        <div className="dso-settings-right">
          {renderContent()}
        </div>
      </div>

      {/* Reset Password Modal with Custom Styling */}
      <Modal show={showResetModal} onHide={handleCloseModal} centered className="custom-modal">
        <Form onSubmit={handlePasswordResetSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FaShieldAlt className="me-2" /> Reset Your Password
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Display Error/Success Messages */}
            {resetError && <Alert variant="danger">{resetError}</Alert>}
            {resetSuccess && <Alert variant="success">{resetSuccess}</Alert>}

            {/* Hide form fields after success */}
            {!resetSuccess && (
              <>
                <Form.Group className="mb-4" controlId="formNewPassword">
                  <Form.Label>New Password</Form.Label>
                  <div className="password-input-wrapper">
                    <Form.Control
                      type="password"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={5}
                    />
                    <FaKey className="input-icon text-muted" />
                  </div>
                  <Form.Text className="text-muted">
                    Password must be at least 5 characters long
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4" controlId="formConfirmPassword">
                  <Form.Label>Confirm New Password</Form.Label>
                  <div className="password-input-wrapper">
                    <Form.Control
                      type="password"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={5}
                    />
                    <FaKey className="input-icon text-muted" />
                  </div>
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} disabled={resetLoading}>
              Cancel
            </Button>
            {/* Hide submit button after success */}
            {!resetSuccess && (
              <Button className="dso-btn dso-btn-primary" variant="" type="submit" disabled={resetLoading}>
                {resetLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Updating...
                  </>
                ) : (
                  'Update Password'
                )}
              </Button>
            )}
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default BackofficeSettings;